import {fetchTapeStatusCount as fetchTapeStatusCountOnce} from "../../../utils/dashboardCalls";
import {
    TAPE_AUDIT_COMPLETE_STATUS_IDS,
    TAPE_DONE_STATUS_IDS,
    TAPE_INGESTION_COMPLETE_STATUS_IDS,
    TAPE_IN_LIBRARY_STATUS_IDS,
    TAPE_READ_FAILED_STATUS_IDS,
    TAPE_READ_IN_PROGRESS_STATUS_IDS,
} from "../../../views/Admin/Job/JobManagement/Processing/processingConfig";

/**
 * Metrics that currently have no backing field in ArkBridge.
 *
 * `man_item_count` filters on tape status, job, destroyed and encrypted state, and can match a
 * phrase in the free-text Metadata field — there is no shipment column to count at all. These stay
 * at zero until a source is added; they are listed here so it is obvious which tiles are not real.
 *
 * Shipment › Returned is NOT in this list: it is sourced, derived from the Physical Stored count.
 * The other two shipment tiles remain unsourced — ArkBridge records no inbound or outbound
 * consignment, only the note left on the item once it has gone.
 *
 * Exceptions › Damaged is no longer in this list either. It is sourced the same way Returned's
 * exclusion is: by matching {@link DAMAGED_NOTE} in Metadata, since the `MediaDamaged` column
 * exists but nothing writes to it. Tracked in media-metrics-status-mapping.md.
 *
 * Both Destruction tiles have now left this list too, sourced the same way — see
 * {@link PHYSICAL_DESTROYED_NOTE}. Only the two inbound/outbound shipment tiles remain unsourced.
 */
const UNSOURCED = {
    shipment: {toTapeArk: 0, return: 0},
};

/**
 * Whether the card is reporting on more than one client at once rather than on one.
 *
 * True for an admin who has selected nothing — the 'All Jobs' position of the header selector. What the
 * card counts at that scope is the sum of the jobs the selector lists, passed as an explicit list of job
 * ids; see ALL_JOBS_SCOPE_NOTE and the `allJobIds` comment in JobContextProvider.
 *
 * That list is the correction to two earlier attempts at this scope. The first showed a prompt in place
 * of the tiles and counted nothing, which left the selector saying 'All Jobs' and the card declining to
 * report them. The second sent neither cid nor job id, which falls through to `_deriveCID()` → 0 and has
 * the fulfiller count every company in `backupsaas_settings` — a platform-wide total that reconciles with
 * nothing on the portal, because ArkBridge holds media for companies Nexus was never given and media that
 * was never onboarded as an order. The figure it produced was three orders of magnitude above the sum of
 * the jobs on screen. Scoping to the selector's job ids answers the objection instead of captioning it:
 * the total is the total of the jobs beside it, so it adds up, and it is still one query per tile rather
 * than one per client because a JobId identifies one job of one company.
 *
 * True for a selection spanning clients as well, since that has no single cid either — but the two are not the
 * same scope, and MediaMetrics separates them as `multiClientSelection`: one is bounded by the ids the reader
 * ticked, the other by every job the portal has onboarded.
 *
 * The export stays withheld at the unbounded scope. The tiles are not: every one of them opens Data Management here,
 * because that page now takes the same selector job ids and lists the media the figure counted
 * (`allJobsScope` in dataManagement.jsx). That includes In Library and Reading, which prefer one job's own
 * page and have no single job to open at this scope — see openJobRunList in MediaMetrics.jsx. The tiles fall
 * back to plain text only when no job at all is onboarded, since then there is nothing to list.
 *
 * Customer users are never at this scope: their cid is their own company.
 *
 * @param {boolean} isAdmin - Whether the signed-in user is an admin.
 * @param {number} [cid] - The resolved company scope (0 and undefined both mean "no one client").
 * @returns {boolean} true when the figures span more than one company.
 */
export const isAllClientsScope = (isAdmin, cid) => Boolean(isAdmin && !cid);

/**
 * What the reader is told when the figures are the sum of every job in the selector.
 *
 * Kept beside the predicate so the two cannot drift. It names the scope rather than warning about it,
 * which is the difference from the caption this replaced: the total is reconcilable, because expanding
 * the selector shows per-job badges that add up to it.
 */
export const ALL_JOBS_SCOPE_NOTE = 'All jobs — every job onboarded to the portal, across all clients';

/**
 * What the reader is told when they have ticked jobs under more than one client.
 *
 * Its own caption because that scope is neither of the two the card had. ALL_JOBS_SCOPE_NOTE would claim
 * every onboarded job when the reader has just narrowed it to a handful, and no caption at all would make a
 * selection spanning clients look identical to a selection inside one — where the difference matters, since
 * these figures add media from separate accounts into one number.
 *
 * The count is the jobs, not the clients: the jobs are what was ticked and what the request is bounded by,
 * and the selector above says which clients they came from.
 *
 * @param {number} jobCount - How many jobs the selection holds.
 */
export const selectedJobsScopeNote = (jobCount) =>
    `Selected jobs — ${jobCount} job${jobCount === 1 ? '' : 's'} ticked in the header, across more than one client`;

/**
 * What the card says when there is no onboarded job to count.
 *
 * The one case ALL_JOBS_SCOPE_NOTE cannot cover: an admin whose customers have no linked ArkBridge job
 * between them, so there is no scope to aggregate. The card reports zeros and says why, rather than
 * dropping the job scope and counting every company in ArkBridge instead — that unscoped total is the
 * number that reconciles with nothing else on the portal, and a card that silently fell back to it would
 * show a figure in the tens of thousands on a portal whose Jobs list is empty.
 */
export const NO_ONBOARDED_JOBS_NOTE = 'No jobs onboarded to the portal yet — nothing to count';

/**
 * Metadata phrase that means the tape has physically left the facility and gone back to its owner.
 *
 * ArkBridge has no disposition column, so this is recorded as free text in the item's Metadata field
 * — the 'Comments' column in the tape table. Matched case-insensitively as a substring, because
 * Metadata routinely holds several notes run together in one field, so an exact comparison would
 * match nothing. Kept here rather than in the fulfiller so the wording can be corrected without a
 * lambda redeploy; if it ever needs to cover variants ('returned to client'), this is the one place.
 */
export const RETURNED_TO_CUSTOMER_NOTE = 'returned to customer';

/**
 * Metadata phrase that means the media is physically damaged.
 *
 * ArkBridge has a `MediaDamaged` column, but nothing writes to it — damage is recorded as free text
 * in the same Metadata field, so the tile counts a phrase rather than reading a flag. Matched
 * case-insensitively, the compare being `lower(MetaData) like '%damaged%'` in the fulfiller.
 *
 * Known limitation of a substring match: 'undamaged' and 'not damaged' both contain 'damaged' and
 * would be counted as damage. Populating `MediaDamaged` is the real fix; until then this is the
 * only available source, and the phrase is one line to change if a convention emerges.
 */
export const DAMAGED_NOTE = 'damaged';

/**
 * Metadata phrase that means the media has a shelf position in the library.
 *
 * ArkBridge does have real location columns — vwItemsAPI exposes LocationType, LocationName,
 * LocationShelf, LocationRow, LocationWarehouse and LocationSite — but `man_item_count` cannot filter
 * on any of them, and LocationType only ever holds 'Box', 'Loose' or 'None'. So this is the same
 * free-text Metadata field the Damaged and Returned phrases live in, matched case-insensitively as a
 * substring.
 *
 * Media carrying this phrase is counted In Library if it has been catalogued — see
 * TAPE_IN_LIBRARY_STATUS_IDS. Nothing else narrows it: the tile used to exclude media whose notes also
 * said 'Damaged', and by requirement no longer does, so a shelved cartridge that was later found
 * damaged is now counted by both tiles at once. Deliberate — the two are different questions, and a
 * damaged tape is still on the shelf — but it does mean the tiles no longer add up to a partition.
 *
 * Deliberately the full two-word phrase and not just 'library': 275 rows carry 'librar' in Metadata,
 * all of it OCR'd text off old seismic tape labels ('RETURN TO SEISMIC FIELD TAPE LIBRARY', 'REMOVED
 * FROM LIBRARY 11-2012'). Matching the single word would report those as shelved media.
 */
export const LIBRARY_LOCATION_NOTE = 'library location';

/**
 * Metadata phrases recording that media has been destroyed — the cartridge, and the cloud copy.
 *
 * `vwItemsAPI` does carry a `destroyed` bit, and `man_item_count` can filter on it, but it reads
 * False on all 302,363 rows in the development database: nothing writes it, the same story as
 * `MediaDamaged`. There has never been a field of any kind for a deleted virtualisation. So both
 * tiles count a phrase in the free-text Metadata field, as specified.
 *
 * The compare is a case-insensitive substring, and case-folded on both sides by the fulfiller —
 * `lower(vi.MetaData) like '%<phrase>%'`, with the phrase itself lowered and trimmed before it is
 * interpolated. These constants are therefore already lower-case: it changes nothing about matching,
 * but it stops the file implying a case-sensitivity that is not there.
 *
 * Two separate counts because they are two separate events. Destroying the cartridge does not delete
 * the cloud copy and deleting the cloud copy does not destroy the cartridge, so a tape may carry
 * either phrase, both, or neither, and the tiles are free to disagree. Neither is filtered by
 * status, matching {@link DAMAGED_NOTE}: destruction is a fact about the media rather than a stage of
 * the pipeline, and media can be destroyed without ever having been read.
 *
 * One word each, as the specification writes them, so a note reading 'Physical Destroyed' with a
 * space will NOT match. There is no controlled vocabulary for this field — it is whatever an operator
 * types — so if both spellings appear in practice, these two lines are where that gets handled.
 *
 * Both constants do double duty: each counts its own Destruction tile, and each is then subtracted from
 * the tiles that claim the media still exists, so a given tape is reported in one place or the other
 * and not both. Nothing in ArkBridge walks a status backwards, so status can never express this on its
 * own — a tape reads as Upload Success forever after one successful upload, and as Read Fail forever
 * after one failed read, whatever later happened to it.
 *
 *   VIRTUAL_DESTROYED_NOTE  → off Virtualized/Uploaded.
 *   PHYSICAL_DESTROYED_NOTE → off Physical Stored, Read Failed and Damaged.
 *
 * Which follows the two events: deleting the cloud copy removes the cloud claim, destroying the
 * cartridge removes the physical ones. Each exclusion is a per-item test in SQL rather than arithmetic
 * on two counts — the same field holds both notes, so the AND matches the row itself — with the single
 * exception of Physical Stored, which is subtracted because it has no exclusion slot left. In Library is
 * the one tile still uncorrected, for want of a third slot; see the fetch comment.
 */
export const PHYSICAL_DESTROYED_NOTE = 'physicaldestroyed';
export const VIRTUAL_DESTROYED_NOTE = 'virtualdestroyed';

/**
 * Tiles whose figure can also be listed: the filter, written once, used by both the count and the list.
 *
 * A tile is a number and a claim about which media it counted. Clicking it opens the tape table filtered
 * to that media, so the claim is now checkable by anyone — a tile reading 12 above a table of 14 rows is
 * a visible contradiction, and there is no way for a reader to tell which of the two is lying.
 *
 * So the criteria are a value, not a call site. The counter below builds its `man_item_count` request
 * from this object and the tape table builds its `man_item_list` request from the same one, which is what
 * makes "the table matches the tile" structural rather than a coincidence that survives until somebody
 * edits one of the two. Adding a phrase or a status to a filter here changes the tile and its table
 * together, and cannot change one of them.
 *
 * `key` is what travels in the URL (`?media=in_library`), so it is a stable identifier rather than the
 * label: a filter can be renamed on screen without invalidating a link somebody has bookmarked.
 *
 * `description` is the whole text of the chip above the filtered table — see mediaFilterChipText, which is
 * what both views call — and, since 2026-08-27, the whole text behind the question mark on the tile that
 * opens it, via mediaFilterHintText. One string per filter, in both places: the tile used to hover its own
 * wording, which said the same thing differently and in a couple of cases said something else. It is copy
 * written for the customer, supplied as such, and it is not a restatement of the criteria above it — all ten
 * were rewritten on 2026-08-26 to say what the media is doing,
 * and what the reader can do about it, rather than which statuses and notes were asked for. So do not read a
 * description as documentation of what the filter matches: the comment on each descriptor is that, and the
 * criteria themselves are the truth. Done Tapes below keeps the old style, because it has no chip — it feeds
 * the Percentage calculation and its description is read by nobody but the next person in this file.
 *
 * Because the description is the whole of both, a filter's name is only shown if its own copy says it, and the
 * copy is what was supplied per filter rather than anything this file assembles. Virtualized/Uploaded opens
 * with `Virtualized / Uploaded : ` because that is how its line was written; Audit/Catalogued, Physical Media
 * and Virtual Media name themselves in passing; the rest do not name themselves at all. So shortening one of
 * these strings is shortening the only thing the reader is told about what they are looking at, and there is
 * no prefix left to fall back on.
 *
 * None of them says "Showing" any more. Virtualized/Uploaded's did, as supplied, until the same string turned
 * up on the tile's hover — where a reader has opened nothing yet and the word is simply wrong. That is the
 * standing cost of one string in two places, and the reason to check both before rewording either.
 *
 * Two consequences worth knowing. Physical Stored's chip no longer mentions that shredded cartridges are left
 * out, and that exclusion is the reason its total can exceed the rows listed under it (see
 * metadataExcludesInBrowser below). And four of the new strings tell the reader to contact support, which is
 * a promise about a support process rather than a description of a query — if that process changes, these
 * strings are part of what changes with it.
 *
 * The fields are named as {@link fetchTapeStatusCount} names its options, so a descriptor spreads
 * straight onto a request; see {@link mediaFilterRequestOptions}.
 */
export const MEDIA_FILTERS = {
    catalogued: {
        key: 'catalogued',
        label: 'Audit/Catalogued',
        /*
         * Status alone, every id from 2 upwards:
         *
         *   StatusID in (2, 3, 4, 5, 6, 7, …)
         *
         * No metadata condition, because the tile makes no claim that needs one. Cataloguing is an event
         * that happened, not a state the media is in: a tape returned to the customer, shredded, or found
         * damaged was still audited, and the count keeps reporting it. Excluding any of those here would
         * make the tile disagree with itself — it is the denominator the bars underneath are drawn
         * against.
         *
         * See TAPE_AUDIT_COMPLETE_STATUS_IDS for why the ids are enumerated rather than compared: neither
         * fulfiller renders anything but `StatusID in (…)`.
         */
        statusIds: TAPE_AUDIT_COMPLETE_STATUS_IDS,
        description: 'Media Audited and Catalogued (includes all media)',
    },
    stored: {
        key: 'stored',
        label: 'Physical Stored',
        /*
         * What the SaaS can be asked:
         *
         *   StatusID in (2, 3, 4, …) AND Metadata does not contain 'returned to customer'
         *
         * Which is the tile's first count exactly — catalogued media that has not gone home. The tile then
         * subtracts a second count for destroyed cartridges, because the fulfiller takes one excluded phrase
         * per request and this filter has spent it on the returned note.
         */
        statusIds: TAPE_AUDIT_COMPLETE_STATUS_IDS,
        metadataExcludes: RETURNED_TO_CUSTOMER_NOTE,
        /*
         * The second exclusion, applied to the listed rows rather than sent with the request.
         *
         * A shredded cartridge is not on a shelf, so the tile does not count it and the list must not show
         * it — but there is no way to ask for both exclusions in one query, and the tile only manages it by
         * subtracting a separate count. The rows are therefore narrowed in the browser, which reaches the
         * same set from the other direction: see mediaFilterMatches.
         *
         * What this costs, and it is worth stating plainly: the total beside the table is the figure BEFORE
         * this exclusion, because it comes from a count query that cannot be told about it either. So an
         * account with destroyed cartridges reads its rows short of its own total — the tile's figure is the
         * short one, and it is the honest one. Sending fewer rows than the total claims is the failure this
         * codebase already chose over the alternative, which is listing media directly beneath a chip saying
         * it has been excluded. The real fix is a fulfiller that accepts a list of excluded phrases; until
         * then a reader comparing the two numbers is seeing the size of the destroyed set, not a bug.
         */
        metadataExcludesInBrowser: PHYSICAL_DESTROYED_NOTE,
        description: 'Catalogued Physical Media stored on-site.',
    },
    virtualized: {
        key: 'virtualized',
        label: 'Virtualized/Uploaded',
        /*
         * Both parts on the one request, which is the whole condition:
         *
         *   StatusID in (4) AND Metadata does not contain 'VirtualDestroyed'
         *
         * The status records that an upload once succeeded and ArkBridge never walks it back, so it
         * answers "has this ever been uploaded" where the tile asks "is this in the cloud now". The
         * exclusion is what turns the first question into the second.
         *
         * Unlike Physical Stored this fits in one query and needs no browser-only second exclusion — it
         * spends the single metadata_excludes slot the fulfiller offers, and has nothing else to spend.
         * So the tile's figure and the listed rows are the same set by construction, with no gap between
         * the total beside the table and the rows under it.
         */
        statusIds: TAPE_INGESTION_COMPLETE_STATUS_IDS,
        metadataExcludes: VIRTUAL_DESTROYED_NOTE,
        /*
         * The one copy that names its own filter before describing it, and it is written here as it was given
         * — spaced slash and space before the colon, which is not how the label above is spelled. Neither is
         * derived from the other, so do not tidy this into the label's spelling.
         *
         * It opened with the word "Showing" until 2026-08-27, which was supplied and then withdrawn once it
         * was seen on the tile's hover: a tooltip that says "Showing …" is describing a table the reader has
         * not opened yet. The word is gone from both places, since both read this one string. Every other
         * filter's copy is its description alone with no name in front of it.
         */
        description: 'Virtualized / Uploaded : Media available in Cloud.',
    },
    in_library: {
        key: 'in_library',
        label: 'In Library',
        /*
         * Both parts of the condition, which the endpoints and:
         *
         *   Metadata contains 'Library Location'
         *   AND StatusID >= 2
         *
         * Reduced to these two by requirement. It previously also excluded media whose notes said
         * 'Damaged', and took the status list as the complement of (4, 7) — so a tape that had finished
         * its read, or had ever been noted as damaged, was not counted as shelved. Both are now in: the
         * question is where the cartridge is, and neither a concluded read nor a damage note moves it off
         * the shelf. The cost is that In Library and Damaged now overlap instead of partitioning.
         *
         * The status list is enumerated from 2 upwards rather than sent as `>= 2`, because neither
         * fulfiller renders anything but `StatusID in (…)`. See TAPE_IN_LIBRARY_STATUS_IDS.
         */
        statusIds: TAPE_IN_LIBRARY_STATUS_IDS,
        metadataIncludes: LIBRARY_LOCATION_NOTE,
        /** Shown on the chip above the filtered table, so the reader knows what they are looking at. */
        description: 'Media moved in Library for processing.',
    },
    reading: {
        key: 'reading',
        label: 'Reading',
        /*
         * Status alone:
         *
         *   StatusID in (6)
         *
         * No metadata condition, deliberately, and not for want of a slot. A read that is running now is
         * a live fact about a drive; the free-text notes record history — damaged, returned, destroyed —
         * and none of it can be true of a tape currently being read. Adding an exclusion would take rows
         * off the table that the drive says are in progress, which is the one thing this tile is for.
         *
         * Which means this descriptor asks exactly what the tile's count already asked before it became
         * clickable, so the figure on the tile cannot have moved as a result of the tile being clickable.
         */
        statusIds: TAPE_READ_IN_PROGRESS_STATUS_IDS,
        description: 'Media in Drive for reading.',
    },
    read_failed: {
        key: 'read_failed',
        label: 'Read Failed',
        /*
         * Both parts on the one request:
         *
         *   StatusID in (13) AND Metadata does not contain 'PhysicalDestroyed'
         *
         * The exclusion has been off and on: dropped once by requirement, on the reading that the tile
         * should report the project's whole failure history, and restored on 2026-08-21 on the reading
         * that a cartridge nobody can pick up any more is not a failure anybody can action. The second
         * reading is the one in force. Settled against real data — item 56096 (PER785L3) sits at status
         * 13 with its notes reading 'PhysicAlDestroyed', and was being counted here.
         *
         * This tile is the reason the exclusion has to be written down at all, and the one place on the
         * card where leaving it out is visible. Every other exception filter matches a phrase, and Metadata
         * holds one note at a time, so those follow the cartridge's latest state for free — a shredded tape
         * simply stops reading 'damaged'. Status 13 is a separate field that ArkBridge never walks back: it
         * says a read once failed and goes on saying it after the cartridge is gone, so nothing but this
         * exclusion takes a destroyed tape back out of the set.
         *
         * Costs no extra request. It spends the fulfiller's single metadata_excludes slot, which this
         * filter had free — unlike Physical Stored, which had spent it on the returned phrase and has to
         * subtract a second count instead. So the figure and the rows the tile opens are the same set by
         * construction, with no gap between the total beside the table and the rows under it.
         */
        statusIds: TAPE_READ_FAILED_STATUS_IDS,
        metadataExcludes: PHYSICAL_DESTROYED_NOTE,
        description: 'Multiple attempts in different drives has failed on Media read operation. '
            + 'Contact support for more information.',
    },
    damaged: {
        key: 'damaged',
        label: 'Damaged',
        /*
         * One phrase, no status:
         *
         *   Metadata contains 'Damaged'
         *
         * The missing status filter is deliberate and has to stay missing. Damage is a property of the
         * cartridge rather than a stage it has reached: a tape can be found damaged before it is ever
         * read, and adding the audit group here would hide exactly those. So the descriptor asks about the
         * media and not about the pipeline.
         *
         * Matched on a phrase because the `MediaDamaged` column exists and nothing writes to it. Still a
         * substring match, and still lowered, because the field holds the note plus its detail —
         * 'Damaged: edge crushed' — rather than the bare word.
         *
         * It also carried the destruction exclusion until that was dropped, and dropping it changes
         * nothing: ArkBridge overwrites Metadata with the current note instead of appending, so a damaged
         * cartridge later shredded reads 'physicaldestroyed' and fails this inclusion already. The
         * exclusion could only ever have removed a row the inclusion had itself removed.
         */
        metadataIncludes: DAMAGED_NOTE,
        description: 'Physically damaged tapes, unrecoverable condition. Contact support for more information.',
    },
    returned: {
        key: 'returned',
        label: 'Returned',
        /*
         * Both parts on the one request:
         *
         *   StatusID in (2, 3, 4, …) AND Metadata contains 'returned to customer'
         *
         * The one clickable tile whose figure is NOT fetched from its own descriptor. Returned is derived
         * as `audited − storedBeforeDestruction`, and Physical Stored is deliberately counted before
         * destroyed media comes off it precisely so that subtraction is available — spending a thirteenth
         * count query to re-ask the same question would be paying for symmetry.
         *
         * Which is sound rather than merely convenient, because the two are the same set by algebra: for
         * A the audit group and P the returned note, |A| − |A ∩ ¬P| = |A ∩ P|, which is this descriptor.
         * The identity holds only while both sides read the same status group and the same phrase — hence
         * the two named constants below rather than a status list written out here, since they are the
         * same two Physical Stored reads. That the two descriptors really do still agree is pinned by a
         * test, because it is the assumption the whole subtraction rests on.
         *
         * The status floor is inherited for the same reason and has the same consequence the derivation
         * has: a cartridge noted as returned while still at status 0 or 1 is in neither figure. That is
         * the intended reading — media that was never catalogued was never in Tape Ark's inventory to
         * send back — and it is now the listed rows as well, so the tile and its table are wrong together
         * rather than separately if the reading is ever revisited.
         */
        statusIds: TAPE_AUDIT_COMPLETE_STATUS_IDS,
        metadataIncludes: RETURNED_TO_CUSTOMER_NOTE,
        description: 'Catalogued media returned to customer.',
    },
    physical_destroyed: {
        key: 'physical_destroyed',
        label: 'Physical Media',
        /*
         * One phrase, no status:
         *
         *   Metadata contains 'PhysicalDestroyed'
         *
         * The same shape as Damaged, for the same reason: destruction is a fact about the cartridge, not a
         * stage it reached. A tape can be shredded before it was ever catalogued or read, and adding the
         * audit group here would hide exactly those. This tile did carry that status group once, and it was
         * removed for that reason — the descriptor keeps it removed rather than reintroducing it, since the
         * request written below it is the same request the count already makes.
         *
         * No exclusion either, and there is nothing sensible to exclude: this note IS the terminal state,
         * so a later note is what would take a row out, and Metadata holds one note at a time. Which also
         * makes the two Destruction filters disjoint in practice — a row can carry either phrase, never
         * both — even though the two events are independent and either can happen without the other.
         *
         * Substring, both sides lowered, like every phrase on this card. Written as one word because that
         * is how the specification writes it: a note reading 'Physical Destroyed' with a space matches
         * neither this filter nor the count. See PHYSICAL_DESTROYED_NOTE.
         */
        metadataIncludes: PHYSICAL_DESTROYED_NOTE,
        description: 'Physical Media destroyed with a Certificate of Destruction. '
            + 'Contact support for the certificate, report or more information.',
    },
    virtual_destroyed: {
        key: 'virtual_destroyed',
        label: 'Virtual Media',
        /*
         * One phrase, no status:
         *
         *   Metadata contains 'VirtualDestroyed'
         *
         * Deliberately not narrowed to media that was ever uploaded, which would be the tempting status
         * filter here — the tile reports what the notes say happened, and a note claiming a cloud copy was
         * deleted for media that never reached status 4 is a data problem worth being able to see rather
         * than one worth filtering out of the list.
         *
         * Independent of the tile above in both directions, so the two figures need not agree and the two
         * lists may overlap for as long as anything holds both phrases: deleting the cloud copy leaves the
         * cartridge on the shelf, and shredding the cartridge leaves the copy in the cloud.
         */
        metadataIncludes: VIRTUAL_DESTROYED_NOTE,
        description: 'Virtualized Media copy deleted from Cloud. Contact support for any report or more information.',
    },
};

/**
 * The filters named, because the tile and the counter reference them directly.
 *
 * Exported as the objects rather than the keys so a typo is a crash at import time instead of a filter
 * that silently resolves to undefined and lists every tape in the job.
 */
export const CATALOGUED_FILTER = MEDIA_FILTERS.catalogued;
export const STORED_FILTER = MEDIA_FILTERS.stored;
export const VIRTUALIZED_FILTER = MEDIA_FILTERS.virtualized;
export const IN_LIBRARY_FILTER = MEDIA_FILTERS.in_library;
export const READING_FILTER = MEDIA_FILTERS.reading;
export const READ_FAILED_FILTER = MEDIA_FILTERS.read_failed;
export const DAMAGED_FILTER = MEDIA_FILTERS.damaged;
export const RETURNED_FILTER = MEDIA_FILTERS.returned;
export const PHYSICAL_DESTROYED_FILTER = MEDIA_FILTERS.physical_destroyed;
export const VIRTUAL_DESTROYED_FILTER = MEDIA_FILTERS.virtual_destroyed;

/**
 * Done Tapes — media that is on a shelf and will have nothing further attempted on it.
 *
 * As specified:
 *
 *   (Metadata contains 'Library Location' AND StatusID in (4, 13))
 *   OR (Metadata contains 'Damaged')
 *
 * Note where the brackets sit. The condition was first written `location AND (status 4/13 OR damaged)`,
 * which cannot match a single row: ArkBridge overwrites MetaData with the cartridge's current note rather
 * than appending, so a tape reading 'damaged' no longer reads 'library location' and fails the AND. Under
 * that reading the damage branch was provably the empty set and this filter collapsed to its first half.
 * Re-read with the brackets above, the damage branch stands alone, needs no shelf position beside it, and
 * survives the overwrite — which is both what the specification meant and the only version of it that does
 * any work. Settled with the user on 2026-08-20.
 *
 * Both phrase tests are contains rather than equals, case-insensitive with both sides lowered — the
 * comparison every phrase on this card uses: `lower(vi.MetaData) like '%<lowered phrase>%'` in the
 * fulfiller, and the constants here are already lower-case. So 'Library Location: R12-S4' matches, and
 * the shelf reference typed after the phrase does not stop it matching.
 *
 * Deliberately NOT in {@link MEDIA_FILTERS}, which is the registry of filters a tile can open as a list.
 * Done Tapes has no tile: it is an input to the Percentage calculation and appears nowhere on screen. Being
 * out of the registry is what keeps `?media=done_tapes` from resolving to a table nothing links to, and
 * keeps `resolveMediaFilter` answering only for filters that have a tile behind them. It carries the same
 * field names regardless, so it spreads onto a request through {@link mediaFilterRequestOptions} like any
 * other descriptor.
 *
 * ── What the request can and cannot carry ──
 *
 * The OR is not expressible in one `man_item_count`: the second branch needs a second `metadata_includes`
 * phrase and the fulfiller takes one. So it takes two counts, and the second one is already being fetched.
 * `Metadata contains 'damaged'` with no status filter is character-for-character the Damaged tile's query,
 * so the tile's figure IS this branch — hence `orFilterKey`, which names where it is borrowed from and is
 * held equal to `orMetadataIncludes` by a test. `buildMediaMetrics` adds the two.
 *
 * Adding them is exact rather than approximate, and for the same reason the original bracketing failed:
 * with one note per row the branches cannot share a tape, so nothing is double-counted. If notes ever
 * accumulate — or an operator types 'Library Location: R12-S4, damaged' as one note — the sum starts
 * over-counting whatever satisfies both, and the overlap (`location AND status 4/13 AND damaged`) has to be
 * subtracted off. That intersection needs two include phrases on one request, which the fulfiller cannot
 * take, so it would be a fourteenth count built the way Physical Stored builds its destroyed subset.
 *
 * ── Where the figure comes from ──
 *
 * Its own request, not arithmetic on In Library. The two share the location phrase, so it is tempting to
 * treat Done Tapes as a share of In Library — but In Library is `StatusID >= 2` and this is two specific
 * statuses inside that, so only SQL can tell them apart. It is fetched on every load and every poll tick
 * along with everything else, which is what keeps it in step with the In Library figure it moves against.
 *
 * ── One consequence, and it is not cosmetic ──
 *
 * Done Tapes is not a subset of In Library. The first branch is; the second is disjoint from it, since a
 * damaged tape's note has replaced its shelf position. The Percentage tile therefore cannot divide by In
 * Library as the formula was written — it would subtract damaged media from a total that does not contain it,
 * reading low by however much damage there is, and reaching 0% under 'All media done' once damage alone
 * exceeds what is outstanding. Measured on company 31 in development: 5 shelved with none concluded and 4
 * damaged elsewhere, rendering as 20% outstanding.
 *
 * The tile's denominator is widened to match this filter instead — `(library location AND status >= 2) OR
 * damaged` — so both halves range over one population and Done Tapes ⊆ the base. That is a departure from the
 * specified division and it is recorded in `buildMediaMetrics` at percentageBase, along with what it costs:
 * the caption's denominator is no longer the In Library tile beside it.
 */
export const DONE_TAPES_FILTER = {
    key: 'done_tapes',
    label: 'Done Tapes',
    statusIds: TAPE_DONE_STATUS_IDS,
    metadataIncludes: LIBRARY_LOCATION_NOTE,
    /*
     * The second branch of the OR — live, and counted, but not from this descriptor.
     *
     * Still not read by mediaFilterRequestOptions or mediaFilterMatches, because a single `man_item_count`
     * takes one include phrase and the first branch has it. The branch is satisfied instead by the count the
     * Damaged tile already fetches, which asks exactly this and nothing more; `orFilterKey` below names that
     * tile so the borrowing is written down where either half would be edited.
     */
    orMetadataIncludes: DAMAGED_NOTE,
    /*
     * Which tile's count stands in for the branch above.
     *
     * A key rather than the descriptor itself, to avoid a reference cycle through MEDIA_FILTERS while it is
     * still being defined. Its whole purpose is the test that resolves it and asserts the tile really does
     * ask `Metadata contains 'damaged'` with no status filter — the day someone narrows the Damaged tile to
     * catalogued media, the Percentage figure silently changes with it, and that test is what says so.
     */
    orFilterKey: 'damaged',
    description: 'Shelved media whose processing has concluded, uploaded or failed',
};

/**
 * Resolves a filter key arriving from outside — a URL parameter — to its descriptor.
 *
 * Returns undefined for anything unrecognised rather than throwing, because the input is a query string
 * a user can edit: a stale bookmark should show the unfiltered table, not an error page. Undefined is
 * also what a caller with no filter passes, so the whole path is the same one.
 *
 * @param {string} [key] - A {@link MEDIA_FILTERS} key, case- and whitespace-insensitive.
 */
export const resolveMediaFilter = (key) => (typeof key === 'string' ? MEDIA_FILTERS[key.trim().toLowerCase()] : undefined);

/**
 * The text of the chip above a filtered table — the one place it is decided.
 *
 * The description and nothing else. It used to be `Showing <label>: <description>`, and the supplied copy was
 * read as replacing only the half after the colon; it was not. Each line given was the whole chip, so nothing
 * is assembled around it: Physical Stored's chip is its copy alone, and Virtualized/Uploaded's names itself
 * first because that is written into its copy rather than derived from its label. No chip says "Showing" now
 * — the one that did was the one string the tile hover also shows, where the word made no sense.
 *
 * What that costs is the filter's name on the nine chips whose copy does not state it. The funnel icon and the
 * dismiss cross still say a filter is on, and the tile the reader clicked to get here is on the page above; the
 * name is not.
 *
 * Both pages that draw the chip call this rather than building the string themselves, so a filter cannot read
 * one way on the Processing tab and another on Data Management — which is how the prefix came to be spelled out
 * twice in the first place.
 *
 * @param {object} [filter] - A {@link MEDIA_FILTERS} descriptor, or undefined.
 * @returns {string} The chip's label, or '' for no filter — callers only render a chip when there is one.
 */
export const mediaFilterChipText = (filter) => (filter ? filter.description : '');

/**
 * The text behind the question mark on a tile — the same words as the chip, by construction.
 *
 * The tile and the chip had written their own copy separately: the Audit/Catalogued tile hovered
 * "Media that has been audited and catalogued." while its chip read "Media Audited and Catalogued (includes
 * all media)", and the other eight said something longer again. Two strings for one filter is two things to
 * keep in step, and they were not in step — a reader clicking a tile was told what they were about to see in
 * one set of words and what they were looking at in another.
 *
 * So this delegates rather than reading `description` itself: the hover cannot come to differ from the chip
 * without this line being changed on purpose.
 *
 * What was given up to get there, because it was not nothing. The old hints explained the query behind the
 * figure — which statuses, which phrase, why a tile can read 0 until a note starts being written, and which
 * count each progress bar is drawn against. The supplied copy says none of that, and it is customer copy
 * rather than an operator's note, so it should not be made to. That material is now only in the comment on
 * each descriptor above, which is where anyone maintaining the card reads it; it is no longer on screen.
 *
 * Only the tiles with a filter behind them use this. Encrypted and Percentage keep hints of their own — they
 * have no filter, and nothing to be consistent with.
 *
 * @param {object} [filter] - A {@link MEDIA_FILTERS} descriptor, or undefined.
 * @returns {string} The hover text, or '' for no filter.
 */
export const mediaFilterHintText = (filter) => mediaFilterChipText(filter);

/**
 * A descriptor as request options, spreadable onto a count or a list call.
 *
 * `{}` for no filter, so an unfiltered caller sends no filter parameters at all rather than sending
 * them as undefined — which the API layer would drop anyway, but which would put `status_ids` into the
 * cache key and split the cache for no reason.
 *
 * @param {object} [filter] - A {@link MEDIA_FILTERS} descriptor, or undefined.
 */
export const mediaFilterRequestOptions = (filter) => (filter
    ? {statusIds: filter.statusIds, metadataIncludes: filter.metadataIncludes, metadataExcludes: filter.metadataExcludes}
    : {});

/**
 * Whether one listed row satisfies a filter — the same tests the SQL applies, in the browser.
 *
 * A second line of defence, not the filter itself. `man_item_list` learned the two phrase parameters in
 * the same change that added this, and an older SaaS ignores an unrecognised parameter rather than
 * rejecting it: the request succeeds, the filter is silently dropped, and the table fills with every tape
 * in the job under a heading saying it is showing the media a tile counted. Re-testing each row means the
 * worst an undeployed SaaS can do is show too few rows on a page — visibly odd, and the right way round,
 * because the alternative is media the reader has been told is not there.
 *
 * One test here has no SQL counterpart at all: `metadataExcludesInBrowser` is a condition the fulfiller
 * cannot be sent, because it takes a single excluded phrase per request and the descriptor's other exclusion
 * has already claimed it. Applied here, it narrows the rows to what the tile counted — the tile reaches the
 * same set by subtracting a second count, which is a trick available to a number and not to a list.
 *
 * Otherwise kept deliberately identical to the SQL, including where it looks asymmetric:
 *   - a row with no notes at all passes the exclusion (`MetaData is null or not like`) and fails the
 *     inclusion (`lower(NULL) like` is never true), which is what the two clauses do;
 *   - both sides are lowered, matching `lower(vi.MetaData) like '%<lowered phrase>%'`;
 *   - substring, not equality, because the field accumulates several notes.
 *
 * @param {object} [filter] - A {@link MEDIA_FILTERS} descriptor; undefined matches everything.
 * @param {object} row - A `man_item_list` row.
 */
export const mediaFilterMatches = (filter, row) => {
    if (!filter) return true;
    const notes = String(row?.metadata ?? '').toLowerCase();
    if (filter.metadataIncludes && !notes.includes(String(filter.metadataIncludes).toLowerCase())) return false;
    if (filter.metadataExcludes && notes.includes(String(filter.metadataExcludes).toLowerCase())) return false;
    if (filter.metadataExcludesInBrowser && notes.includes(String(filter.metadataExcludesInBrowser).toLowerCase())) return false;
    if (Array.isArray(filter.statusIds) && filter.statusIds.length > 0) {
        return filter.statusIds.map(Number).includes(Number(row?.status_id));
    }
    return true;
};

/** Percentage of `total` represented by `value`, clamped to 0–100. Zero when total is 0. */
const percentOf = (value, total) => {
    if (!total || total <= 0) return 0;
    return Math.min(100, Math.max(0, Math.round((value / total) * 100)));
};


/**
 * Shapes the counts into the object {@link MediaMetrics} renders.
 *
 * Physical Stored is its own count, not arithmetic on the others: it is the Audit/Catalogued set
 * minus any tape whose Metadata says it has been returned to the customer, less again anything since
 * destroyed. Audit/Catalogued itself keeps counting those tapes, because the audit did happen —
 * sending a tape home does not un-catalogue it, and neither does shredding it. Falls back to the audit
 * count when the caller does not supply it, which is the honest no-exclusions answer rather than a
 * guess.
 *
 * @param {number} [physicalDestroyed] - Media whose Metadata note records the cartridge destroyed.
 *        Named for the phrase it counts rather than the `destroyed` column it used to read, which is
 *        never written; see {@link PHYSICAL_DESTROYED_NOTE}. Reported by the Destruction tile only —
 *        it is an account-wide count with no status or return filter, so it is NOT what gets taken off
 *        Physical Stored; `storedDestroyed` is.
 * @param {number} [virtualDestroyed] - Media whose Metadata note records the cloud copy deleted.
 *        Independent of the above: neither implies the other.
 * @param {number} encrypted - Media whose RFID transponder reports the tape as encrypted.
 * @param {number} damaged - Media whose Metadata note reports physical damage. Already excludes
 *        destroyed media, which the request does per-item rather than by subtracting here. Read three
 *        times over: the Damaged tile, and both halves of the Percentage figure — it is the second
 *        branch of Done Tapes and, for the same reason, part of that figure's denominator. See
 *        percentageBase.
 * @param {number} [physicalStored] - Catalogued media not returned to the customer, BEFORE destroyed
 *        media is taken off. Kept pre-subtraction because Shipment › Returned is derived from it.
 * @param {number} [storedDestroyed] - The subset of the above whose notes record the cartridge
 *        destroyed. Subtracted from Physical Stored; see the widget's fetch comment for why this is a
 *        request of its own rather than the Destruction figure reused.
 * @param {number} [inLibrary] - Catalogued media with a library location in its notes.
 *        Defaults to 0 rather than to `total`: it is its own filtered count now, derivable from none
 *        of the others, so a caller that supplies metrics directly has no basis for a figure here and
 *        0 is the honest answer.
 * @param {number} [doneTapes] - Shelved media whose processing has concluded — see DONE_TAPES_FILTER. On
 *        the returned object but on no tile: it is an input to the Percentage figure. Defaults to 0 for
 *        the same reason In Library does, being its own count and derivable from no other.
 */
export const buildMediaMetrics = ({total, audited, virtualized, inProgress, readFailed, physicalDestroyed = 0, virtualDestroyed = 0, encrypted = 0, damaged = 0, physicalStored, storedDestroyed = 0, inLibrary = 0, doneTapes = 0}) => {
    /*
     * Physical Stored in two steps, because the two figures answer different questions and one of them
     * is needed further down.
     *
     * `storedBeforeDestruction` is what was counted: catalogued, not returned. `storedOnSite` takes off
     * the destroyed subset, which is what the tile is asked to show — a shredded cartridge is not on the
     * shelf. The subtraction is exact rather than approximate: the destroyed count is fetched with the
     * same status and return filters anded onto it, so it is a subset of the figure it comes off, and
     * for B ⊆ A, |A| − |B| = |A \ B|. Done as arithmetic only because the fulfiller takes a single
     * metadata_excludes phrase per request and that slot already holds the returned phrase; every other
     * tile that needed this exclusion got it per-item in SQL.
     *
     * The clamp is belt and braces for the case where the two counts are read a moment apart and a note
     * lands between them — the failure mode arithmetic has and a per-item match does not.
     */
    const storedBeforeDestruction = Math.max(0, physicalStored ?? audited);
    const storedOnSite = Math.max(0, storedBeforeDestruction - storedDestroyed);

    /*
     * Shipment › Returned is the exact complement of Physical Stored, so it is derived rather than
     * counted. Physical Stored asks for `status >= 2 and metadata not like '%returned to
     * customer%'`; subtracting it from the same audit total leaves precisely the rows that DO carry
     * the note. That is the same answer a ninth `man_item_count` would give, without the request,
     * and it makes the two tiles arithmetically incapable of disagreeing.
     *
     * The consequence worth knowing: this counts returned media within the audit group only. A tape
     * marked returned while still at status 0 or 1 would not appear — which is the intended reading,
     * since a tape that was never catalogued was never in TapeArk's custody to send back. If the
     * tile is ever wanted across every status, it needs its own count with a `metadata_includes`
     * filter, which the fulfiller does not yet have.
     *
     * Derived from `storedBeforeDestruction`, NOT from the tile figure. This matters: the moment
     * destroyed media stopped counting as stored, subtracting the tile would have booked every shredded
     * cartridge as returned to the customer — the two most opposite things that can happen to a tape.
     * Returned means the note says returned, and nothing else does.
     *
     * Falls to 0 when physicalStored was not fetched, because storedBeforeDestruction then equals
     * audited. That is the honest answer: nothing is known to have been returned.
     */
    const returned = Math.max(0, audited - storedBeforeDestruction);

    /*
     * Done Tapes, both branches of it:
     *
     *   (Metadata contains 'library location' AND StatusID in (4, 13))   ← doneTapes, its own count
     *   OR (Metadata contains 'damaged')                                 ← damaged, the tile's own count
     *
     * Added rather than fetched a third time, because the second branch is character-for-character the
     * Damaged tile's query — the damage phrase, no status filter — and that count is already on this load.
     * DONE_TAPES_FILTER.orFilterKey names the tile it is borrowed from so the two cannot drift apart
     * unnoticed; a test holds them equal.
     *
     * Adding is exact, not an approximation, and only because ArkBridge overwrites MetaData with the
     * cartridge's current note instead of appending: a row reading 'damaged' does not also read 'library
     * location', so the two branches cannot share a tape and nothing is counted twice. The day notes
     * accumulate, this needs the overlap — shelved AND concluded AND damaged — subtracted back off, and
     * that intersection is a fourteenth count nobody has asked for yet. See DONE_TAPES_FILTER.
     */
    const doneTapesTotal = doneTapes + damaged;

    /*
     * The Percentage tile's denominator: the population Done Tapes is drawn from.
     *
     *   (Metadata contains 'library location' AND StatusID >= 2)   ← inLibrary
     *   OR (Metadata contains 'damaged')                           ← damaged
     *
     * The same OR the numerator uses, which is the point of it. This was In Library alone, exactly as the
     * formula was specified, and that could not be arithmetically right: Done Tapes is not a subset of In
     * Library. A damaged cartridge's note REPLACES its shelf position rather than joining it, so every
     * damaged tape is in Done Tapes and absent from In Library, and the subtraction took media off a total
     * that never held it. Measured on company 31 in development: 5 shelved, none of them concluded, 4
     * damaged elsewhere in the account — which rendered as 20% outstanding, i.e. '80% of the shelf is done'
     * about a shelf where nothing was, and a fifth damaged tape would have clamped it to 0% under the
     * caption 'All media done'.
     *
     * Widening restores the subset relationship the arithmetic needs. Branch one of Done Tapes is `location
     * AND status in (4, 13)`, which sits inside `location AND status >= 2`; branch two is the same damage
     * note on both sides. So doneTapesTotal <= this by construction, the figure cannot exceed 100%, and the
     * clamp below is back to guarding only the race it was written for.
     *
     * Added rather than counted a fourteenth time, on precisely the assumption the numerator already makes:
     * ArkBridge overwrites MetaData instead of appending, so no row reads both 'damaged' and 'library
     * location' and the two sets are disjoint. Verified on development — the intersection is empty. The day
     * notes accumulate, this and doneTapesTotal need the same overlap subtracted off, and it is one shared
     * count serving both rather than two.
     *
     * The cost, and it is real: this is no longer the In Library figure two tiles along, so the caption's
     * denominator can no longer be reconciled against that tile by eye. See outstandingLabel and the tile's
     * hint, which is where that is explained.
     */
    const percentageBase = inLibrary + damaged;

    /*
     * The Percentage tile's numerator: media in that population which has not concluded.
     *
     * Clamped for the race it was always clamped for — the counts are separate queries a moment apart, so a
     * tape concluding between them could briefly make Done Tapes exceed the base. It no longer absorbs a
     * standing mismatch as well, because there is no longer one to absorb.
     */
    const shelvedOutstanding = Math.max(0, percentageBase - doneTapesTotal);

    /*
     * The Percentage tile's caption. Four states, because 'N of M' is only worth reading in one of them.
     *
     * The two numbers exist to show the figure's working, and at either end of the range they stop doing
     * that and start looking like a fault: '1 of 1 media outstanding' is a fraction written out for no
     * reason, and '0 of 300' invites the reader to hunt for what went wrong rather than telling them the
     * shelf is clear. Both ends are already unambiguous from the percentage — 100% and 0% — so the caption
     * spends its line on the news instead.
     *
     * No count in the two endpoint sentences, deliberately: In Library sits two tiles away and is the only
     * number they could carry. Saying 'All 1 media done' also forces a plural this card has no machinery
     * for, and 'media' as a mass noun reads wrong at exactly the size where someone is checking by hand.
     *
     * The denominator here is percentageBase, not In Library. Since the two differ by however much media is
     * damaged, the caption and the In Library tile will not reconcile on an account holding any — '5 of 9'
     * beside a tile reading 5. That is the honest pair: 9 is the population the percentage is over, and the
     * tile's hint says so. The alternative was a caption that shows the working of a figure it is not the
     * working of, which is the fault this replaced.
     */
    const outstandingLabel = (() => {
        // Nothing shelved and nothing damaged. Distinguished from a cleared shelf, which is the same 0% and
        // the opposite news.
        if (percentageBase <= 0) return 'No media has a library location yet';
        if (shelvedOutstanding <= 0) return 'All media done';
        if (shelvedOutstanding >= percentageBase) return 'Nothing done yet';
        return `${shelvedOutstanding.toLocaleString('en-US')} of ${percentageBase.toLocaleString('en-US')} media outstanding`;
    })();

    return {
        inventory: {
            audited,
            auditedProgress: percentOf(audited, total),
            storedOnSite,
            /*
             * Physical Stored as a share of the account total, whole number, as in the design.
             *
             * Measured against `total` and not against `audited`, which is what the caption says in so
             * many words — the number is followed by 'of Total', so it has to be of the total.
             *
             * The consequence to know: media still at status 0 or 1 has been received but not yet
             * catalogued, so it counts in the denominator and not the numerator. A facility with
             * every catalogued tape on the shelf therefore reads below 100% while anything is
             * awaiting audit. That is the honest reading, and audit coverage is already its own bar
             * on the Audit/Catalogued tile.
             *
             * Note the bars no longer share one denominator: Virtualized and Returned are measured against
             * the catalogued count, this one and the Percentage tile against the account total. Deliberate,
             * and it splits along what each tile is asking. Progress through identified work — uploading,
             * sending back — is a fraction of what has been catalogued, and its bar is meant to reach 100%.
             * A share of the account is a share of the account. This is the only one of the four that spells
             * its denominator on screen, which is why it is the one that must not move.
             */
            storedOnSiteCaption: `${percentOf(storedOnSite, total)}% of Total`,
            virtualized,
            /*
             * Uploading progress against the catalogued count, not against the account total, as asked for.
             *
             * The base is the work there is to do. Cataloguing is what puts media into the pipeline, and
             * every uploaded tape was catalogued first — status 4 is inside the audit group — so
             * `virtualized ⊆ audited` and the bar is a true fraction that can reach 100%. Against `total`
             * it could not: media at status 0 or 1 has been received but not yet catalogued, so a project
             * with every catalogued tape uploaded still read short by whatever was awaiting audit, and the
             * bar never filled while intake continued. That is a reading of intake, not of uploading, and
             * the Audit/Catalogued bar beside it already answers it.
             *
             * Reads 0 when nothing has been catalogued, which is percentOf's zero-denominator answer and
             * the right one: with no work identified there is no progress to report.
             *
             * This is now the only figure on the card reading virtualization coverage. The Percentage tile
             * lower down measures outstanding shelved media, caption included, so the two answer different
             * questions rather than the same one twice.
             */
            virtualizedProgress: percentOf(virtualized, audited),
        },
        virtualization: {
            // Catalogued media with a shelf position in its notes. Its own filtered count, not the
            // account total it started as.
            inLibrary,
            inProgressCount: inProgress,
            /*
             * The Percentage tile's caption — built above, from the shelf widened by the damage note.
             *
             * It has to be these two numbers. The caption read `virtualized / total` when the figure did,
             * and was briefly left that way after the figure changed — which put '1 of 6 media
             * virtualized', a statement about the whole account, under a percentage measured on the
             * library shelf. Both numbers were correct and the pair was unreadable: 1 of 6 is 17%, printed
             * under 0%. The caption's job is to show the working of the figure above it.
             */
            outstandingLabel,
            /*
             * The figure and its bar:  ((base − Done Tapes) / base) × 100, where base is In Library widened
             * by the damage note — see percentageBase.
             *
             * The numerator is the media that has NOT yet finished — anything at a status between catalogued
             * and uploaded. That makes this the one figure on the card that counts DOWN as work is done: it
             * reads 100% when a shelf full of media is untouched and 0% when every tape on it has concluded.
             * Everything else here fills up.
             *
             * percentOf answers 0% for the zero denominator, which is right — nothing tracked is nothing
             * outstanding — and is what every account reads until a library location is first written. Note
             * that 0% therefore has two quite different meanings, and only the caption tells them apart:
             * an empty shelf and a finished one.
             *
             * Specified as `((In Library − Done Tapes) / In Library) × 100`, and the denominator is the one
             * departure from that, deliberately. Done Tapes is `(location AND status 4/13) OR damaged`, and a
             * damaged tape's note replaces its shelf position, so the second branch is disjoint from In
             * Library entirely — the specified division subtracts media its denominator never held, and read
             * 20% on an account where nothing on the shelf was done. Both halves now range over the same
             * population, so Done Tapes ⊆ base and the figure is a true fraction. See percentageBase for the
             * measurement and for what it costs.
             */
            outstandingProgress: percentOf(shelvedOutstanding, percentageBase),
            statusLabel: inProgress > 0 ? 'In Progress' : 'Idle',
        },
        exceptions: {
            readFailed,
            encrypted,
            damaged,
        },
        destruction: {
            physicalMedia: physicalDestroyed,
            virtualMedia: virtualDestroyed,
        },
        shipment: {
            ...UNSOURCED.shipment,
            returned,
            /*
             * Against the catalogued count, as asked for, so the bar fills when everything catalogued has
             * gone home rather than stopping short of 100% forever.
             *
             * The same reasoning as the Virtualized bar, and here the numerator makes it unavoidable:
             * `returned` is derived as `audited − physicalStored`, so it can only ever count media inside
             * the audit group — a cartridge sent back before it was catalogued does not appear in it at all.
             * A numerator drawn from the catalogued set over a denominator of the whole account is not a
             * fraction of anything, and it meant a fully returned project read below 100% whenever
             * anything was still awaiting audit.
             *
             * Reads 0 with nothing catalogued, which is also what `returned` itself is then.
             */
            returnedProgress: percentOf(returned, audited),
        },
        /*
         * Done Tapes: half the subtrahend behind the Percentage tile, and a figure no tile displays.
         *
         * The first branch of the OR only — shelved and concluded. The other half is `exceptions.damaged`,
         * which has a tile of its own, and `doneTapesTotal` below is the two added. Split this way because
         * that is how they are fetched, and because the split is the thing to look at when the percentage
         * looks wrong: the branches come from different queries and only one of them is inside In Library.
         *
         * Top level rather than inside one of the five sections, because the sections are the five groups of
         * tiles on the card and this belongs to none of them. A reader looking for where it is rendered
         * should find that it is not rendered anywhere — only subtracted, in virtualization above.
         *
         * Being on this object is what makes it refresh, and the mechanism is worth stating because it is
         * indirect: the widget's poll tick compares the whole built object against the one on screen and
         * commits only when they differ (see the refresh comment in MediaMetrics.jsx). So a change in this
         * figure counts as a change worth committing, and it is re-read on every tick of the dashboard and
         * of the processing tab. Held anywhere other than here — in its own state, memoised against In
         * Library — it could go stale against the counts beside it.
         *
         * Kept here as well as consumed above so it can be read on its own. virtualization.outstanding*
         * carries the subtraction and In Library carries the minuend, but neither lets a reader recover
         * this number, and a percentage whose inputs cannot be inspected is hard to trust when it looks
         * wrong. Also why a tick that moves only this figure still commits: the redraw is invisible, but
         * the alternative is the Percentage tile computing from something nothing keeps current.
         */
        doneTapes,
        /*
         * Both branches added — the number actually taken off In Library.
         *
         * Exposed beside its two halves because it is the one figure in the sum that appears in no query and
         * on no tile, so without it a reader can see In Library, see Damaged, and still not reconstruct the
         * percentage. It is also where the mismatch shows up: this exceeding In Library is not a fault, it is
         * damaged media being subtracted from a shelf it is not on, and seeing the two numbers side by side is
         * how anyone would work that out.
         */
        doneTapesTotal,
    };
};

/**
 * One count, asked a second time if the first attempt does not come back.
 *
 * Named for the export it wraps, and the export is aliased on the import above, so the thirteen call sites
 * below read as the plain count they always did — there is no count on this card that should not be retried,
 * so a reader picking one out by a different name would be reading a distinction that does not exist.
 *
 * It exists because of how the thirteen fail together. They go out at once, and on the dev deployment they are
 * each 7–14 seconds long: browser → the UI container's nginx → the API app → Twingate → the SaaS endpoint. One
 * of the thirteen came back 502 with an empty body from that nginx, having never reached the API at all — the
 * API logged the other twelve, every one a 200. Under `Promise.all` that single dropped connection discarded
 * all twelve good answers, and every tile on the card read 0: not "nothing is catalogued" but "we could not
 * ask", shown in the one form a reader cannot tell from a real answer.
 *
 * Retried immediately rather than after a pause, on purpose. What failed was getting a connection, not the
 * query — a second attempt needs a new connection, not a quieter moment, and its twelve siblings are still in
 * flight either way so a delay would not lighten anything. It also keeps this a plain promise chain: the
 * refresh tests run on fake timers, and a backoff built on setTimeout would sit unfired inside them.
 *
 * A cancellation is rethrown untouched. That is the card unmounting or its scope changing — the answer is no
 * longer wanted, and asking again would be the one thing worse than not answering.
 *
 * Two attempts, not more: a second failure is no longer a dropped connection but something wrong, and the
 * minute-by-minute refresh is already the mechanism for trying again later.
 *
 * Promise.resolve around the call rather than .catch straight off it, so a stub that answers with something
 * other than a promise still works — Promise.all took whatever it was given, and harnesses that do not care
 * about the counts mock this as a bare jest.fn(), which returns undefined.
 */
const fetchTapeStatusCount = (statusIds, cid, options) => Promise.resolve(fetchTapeStatusCountOnce(statusIds, cid, options))
    .catch(error => {
        if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') throw error;
        console.warn("A media metrics count failed; asking once more:", error?.message ?? error);
        return fetchTapeStatusCountOnce(statusIds, cid, options);
    });

/**
 * Retrieves the Media Metrics counts and pushes the rendered shape into state.
 *
 * Thirteen parallel `man_item_count` calls — the same request type the Job Management progress bars
 * already use — each with a different filter. Eleven tiles, one subtrahend and one figure with no tile:
 * Physical Stored takes two, because destroyed media has to come off it and the fulfiller allows only one
 * excluded phrase per request, and Done Tapes is counted for the Percentage calculation to read rather
 * than to be shown. Scoping is applied by the SaaS fulfiller from the
 * `cid` and `jobIds` we send, matching every other dashboard widget:
 *   - Client user                 → only their own media.
 *   - Admin viewing one customer  → that customer's media, narrowed to the selected job.
 *   - Admin with nothing selected → no cid, and `jobIds` carrying every job in the header selector, so
 *                                   the total is the sum of the jobs onboarded to the portal. The
 *                                   fulfiller applies `vi.JobID in (…)` regardless of company, which is
 *                                   safe because a JobId identifies one job of one company — so this
 *                                   stays one request per tile rather than one per client per tile.
 * Omitting `cid` derives it from the session; with a job list sent, the derived 0 is not what bounds the
 * count. Sending neither is what produced a platform-wide total nothing on the portal could account for.
 *
 * @param {function} setData - Receives the built metrics object.
 * @param {function} setLoading - Toggled around the fetch.
 * @param {number} [cid] - Optional explicit company scope.
 * @param {object} [options] - Passed through to the API layer (e.g. {signal, jobIds}). `cache: false`
 *        is added to every call here and cannot be overridden by a caller — see the note below.
 */
export const mediaMetricsDataRetriever = (setData, setLoading, cid, options = {}) => {
    setLoading(true);

    /*
     * Every count here is read live, bypassing the dashboard cache.
     *
     * `postDashboard` caches man_item_count for CACHE_TTL — ten minutes — in both an in-memory map
     * and sessionStorage, which is the right default for the dropdowns and report widgets it was
     * built for. It is wrong for this card twice over:
     *
     *   - The card asserts it is live, with a green Live Status dot and a minute-by-minute refresh
     *     behind it. Cached, that refresh was re-asking the same question and being handed the same
     *     answer, so an operator watching tapes move through a read saw a figure that did not move
     *     for ten minutes while the dot said otherwise.
     *   - sessionStorage survives a page reload, so the counts did not come back even on a hard
     *     browser refresh — the one escape hatch a user would reach for.
     *
     * The cost is real and was already the accepted ceiling for polling at all: thirteen COUNT queries
     * over vwItemsAPI per minute per open dashboard, none of them now absorbed by the cache. Cheap
     * against reporting a number that is wrong. Note this leaves other dashboard widgets cached —
     * they are not making a liveness claim, and none of them poll.
     */
    const live = {...options, cache: false};

    Promise.all([
        fetchTapeStatusCount(undefined, cid, live),
        // Audit/Catalogued. Read from the descriptor for the same reason Reading is: the tile opens a list
        // of exactly this media, and the only way the number and the list cannot disagree is for neither of
        // them to hold the criteria. The descriptor carries the same status group this line passed before.
        fetchTapeStatusCount(CATALOGUED_FILTER.statusIds, cid, {...live, ...mediaFilterRequestOptions(CATALOGUED_FILTER)}),
        // Virtualized/Uploaded, less anything whose cloud copy has since been deleted. Read from the
        // descriptor because this tile is clickable and opens the same media as a list; the condition and
        // the reason it is written that way are on VIRTUALIZED_FILTER.
        //
        // Done as one anded request rather than by subtracting a second count. Both give the same
        // figure, but the SQL matches the row itself, so there is no arithmetic that could go negative
        // or drift if the two counts were ever read a moment apart — and it is what lets the list ask
        // the identical question, which subtraction could not.
        fetchTapeStatusCount(VIRTUALIZED_FILTER.statusIds, cid, {...live, ...mediaFilterRequestOptions(VIRTUALIZED_FILTER)}),
        // Reading. Read from the descriptor rather than from the status list directly, because this tile is
        // clickable too: the count and the table it opens must be asking the same question, and the only
        // way to guarantee that is for neither of them to hold the criteria. Identical request to the one
        // this line made before — the descriptor carries statuses and nothing else. See MEDIA_FILTERS.
        fetchTapeStatusCount(READING_FILTER.statusIds, cid, {...live, ...mediaFilterRequestOptions(READING_FILTER)}),
        // Read Failed — the failure status, less cartridges since destroyed. Read from the descriptor
        // because this tile is clickable too: the count and the list it opens have to be asking one
        // question, and the only way to guarantee that is for neither of them to hold the criteria. The
        // destruction exclusion is back on this line as of 2026-08-21, and rides on the same request
        // rather than costing a fourteenth; see READ_FAILED_FILTER for why status cannot manage without it.
        fetchTapeStatusCount(READ_FAILED_FILTER.statusIds, cid, {...live, ...mediaFilterRequestOptions(READ_FAILED_FILTER)}),
        // Destruction › Physical Media. A phrase match, not the `destroyed` column: that column is
        // filterable but reads False on every row, so the tile sat at 0 whatever had happened to the
        // media. No status filter now either — it previously carried the audit group, which would have
        // hidden a cartridge destroyed before it was ever catalogued.
        //
        // Read from the descriptor, like every other clickable tile: this is the request the tape list
        // makes when the tile is clicked, and `statusIds` is passed as the descriptor's own undefined
        // rather than a literal, so the absence of a status filter cannot be restored on one of the two.
        fetchTapeStatusCount(PHYSICAL_DESTROYED_FILTER.statusIds, cid, {...live, ...mediaFilterRequestOptions(PHYSICAL_DESTROYED_FILTER)}),
        // Destruction › Virtual Media. Its own count rather than a hard-coded 0, and deliberately not
        // constrained by the physical count: the cloud copy can be deleted with the tape still on the
        // shelf, and the tape can be shredded with the copy still in the cloud. From its own descriptor
        // for the same reason as the line above.
        fetchTapeStatusCount(VIRTUAL_DESTROYED_FILTER.statusIds, cid, {...live, ...mediaFilterRequestOptions(VIRTUAL_DESTROYED_FILTER)}),
        // Encryption is a property of the media, not a stage of the pipeline, so this one carries no
        // status filter: an encrypted tape is worth flagging whatever point it has reached.
        fetchTapeStatusCount(undefined, cid, {...live, encrypted: true}),
        // Physical Stored, in two requests, because destroyed media has to come off it and the one
        // metadata_excludes slot the fulfiller offers is already spent on the returned phrase.
        //
        // First: the audit set less anything sent back to the customer. Counted rather than derived,
        // because "returned" lives in free-text Metadata and only SQL can match it. Note this figure is
        // ALSO what Shipment › Returned is derived from, which is the reason it is kept whole here
        // rather than the exclusion being folded in.
        //
        // Read from the descriptor, like the other clickable tiles: this is the request the tape list makes
        // when the tile is clicked, so the two cannot be written down separately and drift apart.
        fetchTapeStatusCount(STORED_FILTER.statusIds, cid, {...live, ...mediaFilterRequestOptions(STORED_FILTER)}),
        // Second: the destroyed subset of exactly that set, to subtract. The status filter and the
        // returned exclusion are repeated deliberately — that is what makes this a subset of the count
        // above rather than a loosely related figure, so the subtraction lands on the same rows and
        // cannot take off a cartridge that was never in the total. It is why the Destruction tile's
        // count, which is account-wide and unfiltered, is not reused here.
        //
        // The include slot is free on this request, so the two phrases fit as includes-one/excludes-one.
        // A tape both returned and destroyed is not double-counted: it left the first count already.
        //
        // Built from the same descriptor, with its browser-side exclusion turned into this request's include:
        // that is precisely what makes this the destroyed subset of the count above rather than a figure that
        // resembles it, and it means the tile's number and the tile's list are narrowed by one phrase written
        // in one place.
        fetchTapeStatusCount(STORED_FILTER.statusIds, cid, {
            ...live,
            ...mediaFilterRequestOptions(STORED_FILTER),
            metadataIncludes: STORED_FILTER.metadataExcludesInBrowser,
        }),
        // Damaged — the damage note and nothing else. From the descriptor, like Read Failed beside it: this
        // tile opens the same media as a list, so the phrase and the deliberate absence of a status filter
        // are held once, on DAMAGED_FILTER, and read by both requests. `statusIds` is passed as the
        // descriptor's own undefined rather than a literal, so removing the status filter from the
        // descriptor could never leave a stale one on this line.
        fetchTapeStatusCount(DAMAGED_FILTER.statusIds, cid, {...live, ...mediaFilterRequestOptions(DAMAGED_FILTER)}),
        // In Library. The only count on this card that does not spell its own criteria out here: the tile
        // is clickable and opens the tape table filtered to the media behind the number, so the criteria
        // belong to both requests and are held in IN_LIBRARY_FILTER for the two of them to read. See
        // MEDIA_FILTERS for the condition itself and for why it is a value rather than a call site.
        //
        // The one tile that does NOT exclude destroyed media, so a shredded cartridge keeps its shelf
        // position in this count. It used to be impossible — the exclusion slot held the damage phrase —
        // and now it is merely unasked for: dropping that exclusion freed the slot, and putting
        // 'physicaldestroyed' in it would change the figure, which is a decision rather than a tidy-up.
        // Being shared with the table, the tile and its list are wrong together rather than separately.
        fetchTapeStatusCount(IN_LIBRARY_FILTER.statusIds, cid, {...live, ...mediaFilterRequestOptions(IN_LIBRARY_FILTER)}),
        // Done Tapes. The one count on this card with no tile above it: it is an input to the Percentage
        // figure and appears nowhere on screen. Fetched on the same load and the same poll tick as
        // everything else, which is the whole reason it is a request here rather than a value computed and
        // held somewhere — it is a subset of In Library and has to move when In Library does.
        //
        // A subset only SQL can take: In Library is `StatusID >= 2` and this is two statuses inside that
        // range, so there is no arithmetic on the counts already fetched that could produce it.
        //
        // The first branch of the specified OR only. The second — `Metadata contains 'damaged'`, no status —
        // is the Damaged request four lines up, which asks precisely that, so it is added in
        // buildMediaMetrics rather than fetched again here. That is the whole reason this stays at thirteen
        // calls now that the branch is live. See DONE_TAPES_FILTER for why the two can be added at all.
        fetchTapeStatusCount(DONE_TAPES_FILTER.statusIds, cid, {...live, ...mediaFilterRequestOptions(DONE_TAPES_FILTER)}),
    ])
        .then(([total, audited, virtualized, inProgress, readFailed, physicalDestroyed, virtualDestroyed, encrypted, physicalStored, storedDestroyed, damaged, inLibrary, doneTapes]) => {
            setData(buildMediaMetrics({total, audited, virtualized, inProgress, readFailed, physicalDestroyed, virtualDestroyed, encrypted, physicalStored, storedDestroyed, damaged, inLibrary, doneTapes}));
            setLoading(false);
        })
        .catch(error => {
            if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') return;
            console.error("Error fetching media metrics:", error);
            setData(null);
            setLoading(false);
        });
};
